import React, { useState, useEffect } from 'react';
import QuestionBank from './components/QuestionBank';
import CreateAssessment from './components/CreateAssessment';
import AssessmentList from './components/AssessmentList';
import CarveAssessment from './components/CarveAssessment';
import useAssessmentManagementStore from '@/stores/AdminStore/AssessmentManagementStore';
import AssessmentViewById from '@/pages/_shared/AssessmentViewById';
import type { AvailableQuestion } from '@/services/PagesServices/AssessmentManagementService';
import './AssessmentManagement.css';

type TabType = 'question-bank' | 'create' | 'list' | 'carve';

const AssessmentManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('question-bank');
  const [viewAssessmentId, setViewAssessmentId] = useState<number | null>(null);
  const [editAssessmentId, setEditAssessmentId] = useState<number | null>(null);
  const [carveSeed, setCarveSeed] = useState<AvailableQuestion[] | null>(null);

  const {
    questions,
    assessments,
    availableQuestions,
    carveAssessmentSources,
    fetchQuestions,
    fetchAssessments,
    fetchAvailableQuestions,
    fetchCarveAssessmentSources,
  } = useAssessmentManagementStore();

  // Refetch on every tab focus so each tab always shows fresh data after
  // edits made elsewhere. Cached values are intentionally NOT dependencies
  // here to avoid refetch loops (the fetch updates those values).
  useEffect(() => {
    if (activeTab === 'question-bank') {
      fetchQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fetchQuestions]);

  useEffect(() => {
    if (activeTab === 'list') {
      fetchAssessments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fetchAssessments]);

  useEffect(() => {
    // Always refetch on entering the Create tab so newly added question-bank
    // questions show up. Guarding on `!availableQuestions` cached a stale list
    // and dropped questions added after the first load. (availableQuestions is
    // intentionally NOT a dependency here to avoid a refetch loop.)
    if (activeTab === 'create') {
      fetchAvailableQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fetchAvailableQuestions]);

  useEffect(() => {
    if (activeTab === 'carve') {
      fetchCarveAssessmentSources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fetchCarveAssessmentSources]);

  const tabs = [
    { id: 'question-bank' as TabType, label: 'Question Bank' },
    { id: 'create' as TabType, label: 'Create Assessment' },
    { id: 'list' as TabType, label: 'Assessment List' },
    { id: 'carve' as TabType, label: 'Carve Assessment' },
  ];

  const handleAssessmentSaved = () => {
    fetchAssessments();
    setEditAssessmentId(null);
    setCarveSeed(null);
    setActiveTab('list');
  };

  const handleEdit = (id: number) => {
    setEditAssessmentId(id);
    setCarveSeed(null);
    setActiveTab('create');
  };

  const handleCancelCreate = () => {
    setEditAssessmentId(null);
    setCarveSeed(null);
    setActiveTab('list');
  };

  const handleCarve = (chosen: AvailableQuestion[]) => {
    setEditAssessmentId(null);
    setCarveSeed(chosen);
    setActiveTab('create');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'question-bank':
        return <QuestionBank />;
      case 'create':
        return (
          <CreateAssessment
            assessmentId={editAssessmentId ?? undefined}
            seedQuestions={carveSeed ?? undefined}
            onCancel={handleCancelCreate}
            onSaved={handleAssessmentSaved}
            onSeedConsumed={() => setCarveSeed(null)}
          />
        );
      case 'list':
        return (
          <AssessmentList
            onView={(id) => setViewAssessmentId(id)}
            onEdit={handleEdit}
          />
        );
      case 'carve':
        return <CarveAssessment onCarve={handleCarve} />;
      default:
        return <QuestionBank />;
    }
  };

  return (
    <div className="assessment-management">
      <div className="page-header-wrapper">
        <div className="page-header-breadcrumb">
           Content Management › Assessments
        </div>
        <div className="page-header">
          <div>
            <h1 className='page-header-title'>Assessments</h1>
            <p className='page-header-subtitle'>Create and manage quizzes, tests, and surveys</p>
          </div>
          <div>
             {activeTab !== 'create' && (
            <button
              className="create-btn"
              onClick={() => setActiveTab('create')}
            >
              + Create Assessment
            </button>
          )}
          </div>
        </div>
      </div>

      <div className="assessment-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`assessment-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="assessment-content">{renderTabContent()}</div>

      {viewAssessmentId !== null && (
        <AssessmentViewById
          id={viewAssessmentId}
          onClose={() => setViewAssessmentId(null)}
        />
      )}
    </div>
  );
};

export default AssessmentManagement;
